'use client';

import { Download, Share, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { useInstallPrompt } from './useInstallPrompt';

// Machine-pref key (NOT the `lvdr_session_` prefix and not on hardLogout's
// allowlist) so a dismissal survives logout — matching its sibling banners
// (`lvdr:credit-expiry-dismissed:*`). This is a device UI preference, not
// session-tied account data.
const DISMISS_KEY = 'lvdr:pwa-install-dismissed';

/**
 * InstallBanner — a dismissible prompt to install LumaVista to the home screen.
 * Rendered in-flow at the top of the app `<main>`, alongside the other app
 * banners (grace-period, credit-expiry, …). Shows an install button on
 * Android/desktop Chromium and "Add to Home Screen" guidance on iOS Safari.
 * Renders nothing when already installed, on unsupported browsers, or once
 * dismissed.
 */
export function InstallBanner() {
  const t = useTranslations('pwa');
  const { status, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(window.localStorage.getItem(DISMISS_KEY) === '1');
    } catch {
      setDismissed(false);
    }
  }, []);

  if (dismissed) return null;
  if (status !== 'android' && status !== 'ios') return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode / storage disabled — dismiss for this session only */
    }
  };

  return (
    <aside
      aria-label={t(status === 'android' ? 'banner.androidTitle' : 'banner.iosTitle')}
      className="border border-accent/20 bg-accent/10 text-foreground rounded-lg px-4 py-3 text-sm flex items-start gap-3"
    >
      {status === 'android' ? (
        <>
          <div className="flex-1 min-w-0">
            <p className="font-medium">{t('banner.androidTitle')}</p>
            <p className="text-muted-foreground">{t('banner.androidBody')}</p>
          </div>
          <Button size="sm" onClick={promptInstall} className="gap-1.5 shrink-0 self-center">
            <Download className="h-4 w-4" />
            {t('banner.androidAction')}
          </Button>
        </>
      ) : (
        <div className="flex-1 min-w-0 flex items-start gap-2">
          <Share className="h-4 w-4 shrink-0 mt-0.5 text-accent" />
          <div className="min-w-0">
            <p className="font-medium">{t('banner.iosTitle')}</p>
            <p className="text-muted-foreground">{t('banner.iosBody')}</p>
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t('banner.dismissLabel')}
        className="shrink-0 rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="h-4 w-4" />
      </button>
    </aside>
  );
}
